import { useEffect, useState } from "react";
import { CartProvider } from "./cart/CartContext";
import StoreFavicon from "./components/StoreFavicon";
import StoreTheme from "./components/StoreTheme";
import CartPage from "./pages/CartPage";
import CustomerDashboardPage from "./pages/CustomerDashboardPage";
import CustomerOrderDetailPage from "./pages/CustomerOrderDetailPage";
import ContentPage from "./pages/ContentPage";
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import OrderConfirmedPage from "./pages/OrderConfirmedPage";
import ProductDetailPage from "./pages/ProductDetailPage";
import StorefrontPage from "./pages/StorefrontPage";
import StoreBoot from "./components/StoreBoot";
import { resolveStorefrontHost } from "./api/catalog";
import { isPlatformStorefrontHost } from "./lib/platformHosts";
import { configureStorePaths, pathToCustom } from "./lib/storePaths";

function decodePathSegment(value) {
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

function parseStorePath(segments) {
  if (segments.length === 0) {
    return { name: "home" };
  }
  if (segments.length === 1 && segments[0] === "products") {
    return { name: "catalog" };
  }
  if (segments.length === 1 && segments[0] === "cart") {
    return { name: "cart" };
  }
  if (segments.length === 1 && segments[0] === "login") {
    return { name: "login" };
  }
  if (segments.length === 1 && segments[0] === "dashboard") {
    return { name: "dashboard" };
  }
  if (segments.length === 3 && segments[0] === "dashboard" && segments[1] === "orders") {
    const orderId = decodePathSegment(segments[2]);
    if (orderId) return { name: "customer-order", orderId };
  }
  if (segments.length === 1 && segments[0] === "order-confirmed") {
    return { name: "order-confirmed" };
  }
  if (segments.length === 2 && segments[0] === "products") {
    const productHandle = decodePathSegment(segments[1]);
    if (productHandle) return { name: "product", productHandle };
  }
  if (segments.length === 2 && segments[0] === "pages") {
    const pageSlug = decodePathSegment(segments[1]);
    if (pageSlug) return { name: "content", pageSlug };
  }
  return null;
}

function resolvePlatformRoute(pathname) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] !== "commerce" || !segments[1]) return null;
  const storeSlug = decodePathSegment(segments[1]);
  if (!storeSlug) return null;
  const rest = parseStorePath(segments.slice(2));
  if (!rest) return null;
  return { ...rest, storeSlug, urlMode: "atomicpay_path" };
}

function resolveCustomRoute(pathname, storeSlug) {
  const segments = pathname.split("/").filter(Boolean);
  if (segments[0] === "commerce" && segments[1] === storeSlug) {
    const next = pathToCustom(pathname, storeSlug);
    window.history.replaceState({}, "", next);
    return resolveCustomRoute(next, storeSlug);
  }
  const rest = parseStorePath(segments);
  if (!rest) return null;
  return { ...rest, storeSlug, urlMode: "custom_domain" };
}

function renderPage(route) {
  if (route.name === "product") {
    return (
      <ProductDetailPage
        storeSlug={route.storeSlug}
        productHandle={route.productHandle}
      />
    );
  }
  if (route.name === "cart") return <CartPage storeSlug={route.storeSlug} />;
  if (route.name === "login") return <LoginPage storeSlug={route.storeSlug} />;
  if (route.name === "dashboard") {
    return <CustomerDashboardPage storeSlug={route.storeSlug} />;
  }
  if (route.name === "customer-order") {
    return (
      <CustomerOrderDetailPage
        storeSlug={route.storeSlug}
        orderId={route.orderId}
      />
    );
  }
  if (route.name === "order-confirmed") {
    return <OrderConfirmedPage storeSlug={route.storeSlug} />;
  }
  if (route.name === "catalog") {
    return <StorefrontPage storeSlug={route.storeSlug} />;
  }
  if (route.name === "content") {
    return (
      <ContentPage storeSlug={route.storeSlug} pageSlug={route.pageSlug} />
    );
  }
  return <HomePage storeSlug={route.storeSlug} />;
}

function initialPlatformRoute() {
  if (typeof window === "undefined") return null;
  if (!isPlatformStorefrontHost(window.location.hostname)) return null;
  const next = resolvePlatformRoute(window.location.pathname);
  if (next) {
    configureStorePaths({ mode: "atomicpay_path", storeSlug: next.storeSlug });
  }
  return next;
}

export default function App() {
  const [route, setRoute] = useState(initialPlatformRoute);
  const [bootError, setBootError] = useState(null);
  const [resolvingHost, setResolvingHost] = useState(
    () => typeof window !== "undefined" && !isPlatformStorefrontHost(window.location.hostname)
  );

  useEffect(() => {
    const hostname = window.location.hostname;
    const pathname = window.location.pathname;

    if (isPlatformStorefrontHost(hostname)) {
      return;
    }

    let cancelled = false;
    resolveStorefrontHost(hostname)
      .then((data) => {
        if (cancelled) return;
        configureStorePaths({ mode: "custom_domain", storeSlug: data.storeSlug });
        setRoute(resolveCustomRoute(pathname, data.storeSlug));
        setResolvingHost(false);
      })
      .catch((error) => {
        if (!cancelled) {
          setBootError(error);
          setResolvingHost(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (bootError) {
    return (
      <main className="route-error">
        <div className="route-error__mark">A</div>
        <h1>Loja não encontrada</h1>
        <p>Este domínio ainda não está conectado a uma loja AtomicPay ativa.</p>
      </main>
    );
  }

  if (resolvingHost) {
    return <StoreBoot />;
  }

  if (!route) {
    return (
      <main className="route-error">
        <div className="route-error__mark">A</div>
        <h1>Endereço de loja inválido</h1>
        <p>
          Acesse uma loja usando o endereço{" "}
          <code>/commerce/nome-da-loja</code>.
        </p>
      </main>
    );
  }

  return (
    <CartProvider storeSlug={route.storeSlug} key={route.storeSlug}>
      <StoreFavicon storeSlug={route.storeSlug} />
      <StoreTheme storeSlug={route.storeSlug} />
      {renderPage(route)}
    </CartProvider>
  );
}
